import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchEpisodeByNumber } from '../../utils/fgtnApi';
import {
  formatAirDate,
  parseEpisodeNumber,
  sanitizeFgtnHtml,
  isProbablyMissingTableError,
} from '../../utils/fgtn';
import { handleFgtnNotesClick } from '../../utils/fgtnOpenLink';
import { isSupabaseConfigured } from '../../utils/supabaseClient';
import FgtnQrBlock from './FgtnQrBlock';
import './Fgtn.css';

export default function FgtnEpisodePage() {
  const { episodeNumber: episodeParam } = useParams();
  const episodeNumber = parseEpisodeNumber(episodeParam);
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!episodeNumber) {
        setError('That episode number is not valid.');
        setLoading(false);
        return;
      }
      if (!isSupabaseConfigured()) {
        setError('This episode page is not available yet.');
        setLoading(false);
        return;
      }

      try {
        const row = await fetchEpisodeByNumber(episodeNumber);
        if (cancelled) return;
        if (!row) {
          setError(`Episode ${episodeNumber} is not up yet.`);
          setEpisode(null);
        } else {
          setEpisode(row);
          setError('');
        }
      } catch (err) {
        console.error('Failed to load FGTN episode', err);
        if (!cancelled) {
          setError(
            isProbablyMissingTableError(err)
              ? 'FGTN episodes are not set up in the database yet.'
              : 'Could not load this episode right now.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    load();
    return () => {
      cancelled = true;
    };
  }, [episodeNumber]);

  useEffect(() => {
    if (episode) {
      document.title = `FGTN ${episode.episode_number}: ${episode.title} | Angler Built`;
    } else if (episodeNumber) {
      document.title = `FGTN Episode ${episodeNumber} | Angler Built`;
    }
  }, [episode, episodeNumber]);

  const bodyHtml = episode ? sanitizeFgtnHtml(episode.body_html) : '';

  return (
    <div className="fgtn-page">
      <p className="fgtn-kicker">
        <Link to="/fgtn" style={{ color: 'inherit', textDecoration: 'none' }}>
          Fishing Gear & Tech News
        </Link>
      </p>

      {loading && <div className="fgtn-status">Loading episode…</div>}
      {!loading && error && (
        <>
          <h1 className="fgtn-title">{episodeNumber ? `Episode ${episodeNumber}` : 'Episode'}</h1>
          <div className="fgtn-error">{error}</div>
        </>
      )}

      {!loading && episode && (
        <>
          <h1 className="fgtn-title">
            Episode {episode.episode_number}
            <span style={{ display: 'block', marginTop: 8 }}>{episode.title}</span>
          </h1>
          <p className="fgtn-meta">{formatAirDate(episode.air_date)}</p>
          <div className="fgtn-episode-layout">
            <div className="fgtn-card">
              {bodyHtml ? (
                <div
                  className="fgtn-notes"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  onClick={handleFgtnNotesClick}
                />
              ) : (
                <p className="fgtn-subtitle" style={{ margin: 0 }}>
                  Links for this episode will show up here.
                </p>
              )}
            </div>
            <FgtnQrBlock episodeNumber={episode.episode_number} title={episode.title} />
          </div>
        </>
      )}
    </div>
  );
}
